import { css } from '@emotion/react'
import { Tooltip, colors } from 'plume-ui'
import { useRef, useState } from 'react'
import { useUser } from '../../../../contexts/AuthContext'
import { CompanyContactPerson, UsersResult } from '../../../../types/api'
import {
  deleteCompanyContactPerson,
  registerCompanyContactPerson,
} from '@/lib/api/company'
import {
  CONTACT_PERSON_SETTING_ON_COMPANY_PAGE,
  SUM_OF_CONTACT_PERSON_SETTING,
  trackEvent,
} from '@/lib/tracking'
import { IcomoonIcon } from '../../../atoms/IcomoonIcon'
import { useOnClickOutside } from '../../../../hooks/useOnClickOutside'

type Props = {
  shortname: string
  users: UsersResult
  selectedContactPerson: CompanyContactPerson | null
  setSelectedContactPerson: React.Dispatch<
    React.SetStateAction<CompanyContactPerson | null>
  >
}

export const ContactPersonIcon: React.FC<Props> = ({
  shortname,
  selectedContactPerson,
  setSelectedContactPerson,
  users,
}) => {
  const [usersOpening, setUsersOpening] = useState(false)

  const currentUser = useUser()

  const ref = useRef<HTMLDivElement>(null)
  useOnClickOutside([ref], () => setUsersOpening(false))

  const handleChangeCompanyContactPerson = async (
    user: UsersResult['users'][0]
  ) => {
    const preUser = JSON.parse(JSON.stringify(selectedContactPerson))
    setSelectedContactPerson({
      userId: user.id,
      companyId: shortname,
      imageUrl: user.imageUrl,
      firstName: user.firstName,
      lastName: user.lastName,
    })
    await registerCompanyContactPerson(shortname, user.id, () =>
      setSelectedContactPerson(preUser)
    )
    setUsersOpening(false)
    trackEvent(currentUser, CONTACT_PERSON_SETTING_ON_COMPANY_PAGE)
    trackEvent(currentUser, SUM_OF_CONTACT_PERSON_SETTING)
  }

  const handleDeleteCompanyContactPerson = async () => {
    const preUser = JSON.parse(JSON.stringify(selectedContactPerson))
    setSelectedContactPerson(null)
    await deleteCompanyContactPerson(shortname, () =>
      setSelectedContactPerson(preUser)
    )
    setUsersOpening(false)
    trackEvent(currentUser, CONTACT_PERSON_SETTING_ON_COMPANY_PAGE)
    trackEvent(currentUser, SUM_OF_CONTACT_PERSON_SETTING)
  }

  return (
    <div
      className="contact-person"
      css={css`
        min-width: 64px;
        flex-shrink: 0;
        display: flex;
        flex-direction: column;
      `}
      ref={ref}
    >
      <Tooltip
        className="contact-person-icon-tooltip"
        label={
          selectedContactPerson
            ? `${selectedContactPerson.lastName} ${selectedContactPerson.firstName}`
            : '担当者を選択'
        }
        place="top"
      >
        <div
          css={css`
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            border: ${selectedContactPerson
              ? `none`
              : `1px dashed ${colors.white00}`};
            margin: auto;
            position: relative;
            background-color: ${selectedContactPerson
              ? selectedContactPerson.imageUrl
                ? 'transparent'
                : colors.blue50
              : 'transparent'};
            &:hover {
              cursor: pointer;
              &:before {
                background-color: rgba(0, 0, 0, 0.08);
                position: absolute;
                border-radius: 50%;
                top: 0;
                right: 0;
                bottom: 0;
                left: 0;
                content: ' ';
              }
            }
          `}
          onClick={() => setUsersOpening((pre) => !pre)}
        >
          {selectedContactPerson ? (
            selectedContactPerson.imageUrl ? (
              <img
                css={css`
                  width: 40px;
                  height: 40px;
                  object-fit: cover;
                  border-radius: 50%;
                  max-width: unset;
                `}
                src={selectedContactPerson.imageUrl}
              />
            ) : (
              <div
                css={css`
                  font-size: 14px;
                  z-index: 1;
                `}
                className="default"
              >
                {selectedContactPerson.lastName?.slice(0, 2)}
              </div>
            )
          ) : (
            <IcomoonIcon
              name="person"
              css={css`
                font-size: 24px;
                color: white;
                z-index: 1;
              `}
            />
          )}
        </div>
        <div
          css={css`
            margin-top: 4px;
            font-size: 11px;
            line-height: 1.25;
            text-align: center;
          `}
        >
          担当者
        </div>
      </Tooltip>
      {usersOpening && (
        <div
          css={css`
            position: relative;
          `}
          className="users"
        >
          <ul
            css={css`
              width: 216px;
              max-height: 345px;
              margin-top: 7.5px;
              background: ${colors.white00};
              border-radius: 2px;
              box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
              padding: 5px 0;
              position: absolute;
              list-style: none;
              white-space: nowrap;
              transition: 0.2s;
              z-index: 50;
              overflow: auto;
              li:hover {
                background-color: ${colors.gray40};
              }
            `}
          >
            <li
              onClick={async () => handleDeleteCompanyContactPerson()}
              css={css`
                display: flex;
                flex-direction: row;
                align-items: center;
                line-height: 125%;
                letter-spacing: 0.02em;
                gap: 8px;
                font-size: 14px;
                color: ${colors.black30};
                cursor: pointer;
                padding: 7px 16px 7px 36px;
              `}
            >
              {!selectedContactPerson && (
                <IcomoonIcon
                  name={'check1'}
                  css={css`
                    position: absolute;
                    font-size: 12px;
                    left: 12px;
                    z-index: 1;
                    color: ${colors.black30};
                  `}
                />
              )}
              <div
                css={css`
                  font-size: 14px;
                  color: ${colors.black30};
                  padding-left: 8px;
                `}
              >
                （空白）
              </div>
            </li>
            {users.users.map((user) => (
              <li
                key={user.id}
                css={css`
                  display: flex;
                  flex-direction: row;
                  align-items: center;
                  line-height: 125%;
                  letter-spacing: 0.02em;
                  gap: 8px;
                  font-size: 14px;
                  color: ${colors.black30};
                  cursor: pointer;
                  padding: 7px 16px 7px 36px;
                `}
                onClick={async () => handleChangeCompanyContactPerson(user)}
              >
                {selectedContactPerson?.userId === user.id && (
                  <IcomoonIcon
                    name={'check1'}
                    css={css`
                      position: absolute;
                      font-size: 12px;
                      left: 12px;
                      z-index: 1;
                      color: ${colors.black30};
                    `}
                  />
                )}
                <div
                  css={css`
                    font-size: 14px;
                    color: ${colors.black30};
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                  `}
                  className="user-name"
                >
                  {`${user.lastName} ${user.firstName}`}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
